/* eslint-disable @typescript-eslint/no-empty-function */
import { Box, Container, Grid, Tooltip, Typography, Zoom } from '@material-ui/core';
import { Alert } from '@material-ui/lab';
import { Bookmark, CopyToClipboard, OpenGraphTags, Page, SocialShareButtons, StructuredJobData } from 'components';
import { DialogQrCode } from 'components/dialog-qr-code';
import { useAuth } from 'context/AuthContext';
import { useNotifications } from 'context/NotificationsContext';
import createDOMPurify from 'dompurify';
import { GoogleAnaliticsEvent } from 'enum';
import { ApplicationInviteStatus, ContactInfo, Job } from 'graphql/generated';
import { htmlToText } from 'html-to-text';
import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { logAnalyticsEvent } from 'ui-services/analytics.ui-service';
import { getSearchParams } from 'utils/browser-utils';
import { RelatedJobs } from './components';
import { ApplyButton } from './job-apply';
import { useStyle } from './job-detail.style';
import { JobHeader } from './job-header';
import { ContactSummary, JobSummary } from './job-summary';

const DOMPurify = createDOMPurify(window);

export type JobDetailProps = Job & {
    postedDateText: string;
    deadlineText: string;
    salaryText?: string | null;
    bookmarkJob?: any;
    refetch?: any;
};

export const JobDetail: React.FC<JobDetailProps> = (job) => {
    const classes = useStyle();
    const { t } = useTranslation();
    const { title, bookmarkJob, id, bookmarked, descriptionHtml, company, refetch } = job;

    const { isLoggedIn, openModal, isEmployer } = useAuth();
    const { notify } = useNotifications();
    const searchParam = getSearchParams();
    const isSuccessed = searchParam?.show_success_alert;

    const onCopied = (): void => {
        notify({
            message: t('jobDetails:copyLinkSuccess'),
            type: 'success',
        });
    };

    const relatedJobs = job?.relatedJobs?.edges || [];

    const descriptionText = htmlToText(job.descriptionHtml, {
        wordwrap: 130,
    });

    useEffect(() => {
        logAnalyticsEvent(GoogleAnaliticsEvent.JOB_VIEWED, { jobId: id });
    }, []);

    return (
        <Page title={title} style={{ padding: 0 }}>
            <OpenGraphTags
                description={descriptionText.length > 160 ? `${descriptionText.slice(0, 160)}...` : descriptionText}
                title={title}
                url={window.location.href}
                imageUrl={company.logo?.url || ''}
            />
            <StructuredJobData job={job} />
            <JobHeader job={job} refetch={refetch} />
            <Container maxWidth="lg" className={classes.root} style={{ position: 'relative' }}>
                {isSuccessed && (
                    <Box py={2}>
                        <Alert severity="success">{t('jobDetails:slotPaymentSuccess')}</Alert>
                    </Box>
                )}
                {!isEmployer && (
                    <Tooltip
                        TransitionComponent={Zoom}
                        placement="top"
                        arrow
                        title={(bookmarked ? t('jobDetails:removeBookmarkTitle') : t('jobDetails:bookmarkTitle')) || ''}
                    >
                        <Box className={classes.bookmark}>
                            <Bookmark
                                onClick={(): void => {
                                    if (!isLoggedIn) {
                                        openModal();
                                    } else {
                                        bookmarkJob({
                                            variables: {
                                                jobId: id,
                                            },
                                        });
                                    }
                                }}
                                size="medium"
                                bookmarked={bookmarked}
                            />
                        </Box>
                    </Tooltip>
                )}
                <Grid container spacing={1}>
                    <Grid item xs={12} md={8}>
                        <div className={classes.content}>
                            <div className={classes.contentSection}>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: DOMPurify.sanitize(descriptionHtml.replace(/(style=".+?")/gm, '')),
                                    }}
                                />
                            </div>
                        </div>
                    </Grid>
                    <Grid item xs={12} md={4}>
                        <div className={classes.rightSide}>
                            {job.invitation?.status !== ApplicationInviteStatus.Pending && <ApplyButton job={job} />}

                            <div className={classes.sideSection}>
                                <Box className={classes.summary}>
                                    <Box mb="20px">
                                        <JobSummary job={job as Job} />
                                    </Box>
                                    <ContactSummary contacts={job.contacts as ContactInfo} />
                                </Box>
                            </div>
                            <div style={{ marginBottom: '10px' }} className={classes.sideSection}>
                                <Typography variant="h5" color="textSecondary">
                                    {t('jobDetails:share')}
                                </Typography>
                            </div>
                            <div style={{ marginBottom: '10px' }} className={classes.sideSection}>
                                <SocialShareButtons pageType="job" url={window.location.href}>
                                    <DialogQrCode description={t('jobDetails:qrDescription')} />
                                </SocialShareButtons>
                            </div>
                            <div className={classes.sideSection}>
                                <CopyToClipboard onCopied={onCopied} value={window.location.href} />
                            </div>
                        </div>
                    </Grid>
                </Grid>
                {relatedJobs.length > 0 && <RelatedJobs relatedJobs={relatedJobs} />}
            </Container>
        </Page>
    );
};
